import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaLock, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import { setPassword, validateResetToken } from '../services/AuthService';
import { ROUTES } from '../config/constants';
import '../styles/Home.css';
import '../styles/LoginModal.css';

function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setTokenValid(false);
      return;
    }
    validateResetToken(token)
      .then(({ success: ok, valid }) => {
        setIsValidating(false);
        setTokenValid(ok && valid);
      })
      .catch(() => {
        setIsValidating(false);
        setTokenValid(false);
        setError('Impossible de vérifier le lien. Assurez-vous que le backend tourne sur le port 3001.');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 5) {
      setError('Le mot de passe doit contenir au moins 5 caractères');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setIsLoading(true);
    const result = await setPassword({ token, password });
    setIsLoading(false);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        navigate(ROUTES.HOME);
      }, 2000);
    } else {
      setError(result.error || 'Erreur lors de la définition du mot de passe');
    }
  };

  if (isValidating) {
    return (
      <div className="set-password-page">
        <div className="set-password-card">
          <p>Vérification du lien...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="set-password-page">
        <div className="set-password-card">
          <h1>Lien invalide ou expiré</h1>
          <p>
            {error ||
              "Ce lien n'est plus valide. Veuillez demander un nouveau lien de réinitialisation ou de confirmation."}
          </p>
          <button
            type="button"
            className="login-submit-btn"
            onClick={() => navigate(ROUTES.HOME)}
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="set-password-page">
        <div className="set-password-card">
          <h1>Mot de passe défini</h1>
          <p>Vous pouvez maintenant vous connecter.</p>
          <button
            type="button"
            className="login-submit-btn"
            onClick={() => navigate(ROUTES.HOME)}
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="set-password-page">
      <div className="set-password-card login-modal">
        <div className="modal-header">
          <h2>Définir votre mot de passe</h2>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              <div className="input-wrapper has-password-toggle">
                <FaLock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  placeholder="Au moins 5 caractères"
                  disabled={isLoading}
                  required
                  minLength={5}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={
                    showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
                  }
                  disabled={isLoading}
                >
                  {showPassword ? <FaEyeSlash aria-hidden /> : <FaEye aria-hidden />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <div className="input-wrapper has-password-toggle">
                <FaCheck className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Répétez le mot de passe"
                  disabled={isLoading}
                  required
                  minLength={5}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={
                    showConfirmPassword
                      ? 'Masquer la confirmation du mot de passe'
                      : 'Afficher la confirmation du mot de passe'
                  }
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash aria-hidden />
                  ) : (
                    <FaEye aria-hidden />
                  )}
                </button>
              </div>
            </div>
            {error && <div className="error-message">{error}</div>}
            <button
              type="submit"
              className="login-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Enregistrement...' : 'Définir le mot de passe'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SetPasswordPage;
